<template>
    <div>

        <v-alert v-if="!!error" role="alert" type="error" icon="error" dense :value="!!error" class="mb-4">{{ error }}</v-alert>

        <template v-else>
            <h1>{{name}}</h1>
            <p class="mt-3 mb-3">{{ message }}</p>
            <v-spacer></v-spacer>
            <v-spacer></v-spacer>
            <v-layout column align-center>
                <p class="display-3">{{cost}}</p>

                <v-btn color="primary" class="px-5" @click.prevent="purchaseVendible(vendibleId)" :disabled="isRedirecting">
                    <loading v-if="isRedirecting" :message="$t('VENDIBLE_PAYMENT_BUY_BUTTON_REDIRECTING')" />
                    <span v-else><v-icon small class="pr-2">shopping_cart</v-icon>{{ $t('VENDIBLE_PAYMENT_BUY_BUTTON') }}</span>
                </v-btn>

                <v-btn color="primary" text class="mt-2 px-5" @click.prevent="cancel" :disabled="isRedirecting">
                    {{ $t('VENDIBLE_PAYMENT_CANCEL_BUTTON') }}
                </v-btn>
            </v-layout>
            <accessible-expandible class="mt-6 caption" :title="$t('VENDIBLE_PAYMENT_REFUND_POLICY_EXPANSION_HEADER')">
                <div class="mt-2" v-html="refundPolicy"/>
            </accessible-expandible>
        </template>
    </div>
</template>


<script lang="ts">
import { IVendible, IVendiblePaymentRequiredError, VendibleType } from '@cyber-range/cyber-range-api-payment-client';
import Vue from "vue";
import Component from "vue-class-component";
import { Watch } from 'vue-property-decorator';
import LoginButtons from "../login/LoginButtons.vue";

import { useApiClientStore } from "@/stores/apiClientStore";
import { useAuthenticationStore } from "@/stores/authenticationStore";
import Loading from "@utils/Loading.vue";
import AccessibleExpandible from '@components/utils/AccessibleExpandible.vue';
import { useErrorStore } from "@/stores/errorStore";
import { usePaymentStore } from "@/stores/paymentStore";
import { storeToRefs } from "pinia";

@Component({
    components: { Loading, LoginButtons, AccessibleExpandible }
})
export default class PayVendible extends Vue {
    get vendibleApiClient() { return useApiClientStore().vendibleApiClient; }
    get isLoggedIn() { return useAuthenticationStore().isLogin; }
    // TODO: Change to composition api
    error = storeToRefs(useErrorStore()).errorMessage;
    // END TODO

    // Using this instead of StoreGetter.IsLoading because IsLoading becomes false before page finishes redirecting
    isRedirecting: boolean = false;

    @Watch('$route.query.error', { immediate: true, deep: true })
    async onError(errorCode: string)
    {
        if (!errorCode)
        {
            return;
        }

        useErrorStore().setError(new Error(this.$t('VENDIBLE_PAYMENT_ERROR_GENERIC').toString()));
    }

    get vendibleId()
    {
        return this.$route.params.vendibleid;
    }

    vendible: IVendiblePaymentRequiredError | IVendible = null;

    @Watch('vendibleId', { immediate: true })
    async onVendibleIdChanged() {
        const vendiblePaymentRequiredError = usePaymentStore().requiredPaymentData[this.vendibleId];
        if (vendiblePaymentRequiredError) {
            this.vendible = vendiblePaymentRequiredError;
            return
        }

        if (!this.isLoggedIn) {
            this.$router.push(`/?redirect_url=${this.$route.fullPath}`);
            return;
        }

        const vendible = await this.vendibleApiClient.getOne(this.vendibleId);

        if (!vendible) {
            useErrorStore().setError(new Error(this.$t('VENDIBLE_NOT_FOUND_ERROR_MESSAGE').toString()));
        }

        this.vendible = vendible;
    }

    get cost(): string
    {
        return "$" + (this.vendible?.price?.amount || 0).toFixed(2);
    }

    get name(): string
    {
        return this.vendible?.name
    }

    get message(): string
    {
        if (this.vendible?.['startTimestamp'] && this.vendible?.['endTimeStamp'])
        {
            const startDate = new Date(this.vendible['startTimestamp']).toDateString();
            const endDate = new Date(this.vendible['endTimeStamp']).toDateString();

            return this.$t('VENDIBLE_PAYMENT_COURSE_ENROLLMENT_MESSAGE', { name: this.name, startDate, endDate }).toString();
        }
        else if ('type' in this.vendible && this.vendible.type === VendibleType.OrganizationBalance)
        {
            return this.$t('VENDIBLE_PAYMENT_ORGANIZATION_BALANCE_MESSAGE').toString();
        }
        else
        {
            return this.$t('VENDIBLE_PAYMENT_GENERIC_MESSAGE', { name: this.name }).toString();
        }
    }

    get refundPolicy()
    {
        if ('type' in this.vendible && this.vendible.type === VendibleType.OrganizationBalance)
        {
            return this.$t('VENDIBLE_PAYMENT_ORGANIZATION_BALANCE_REFUND_POLICY_EXPANSION_CONTENT');
        }
        else
        {
            return this.$t('VENDIBLE_PAYMENT_REFUND_POLICY_EXPANSION_CONTENT');
        }
    }

    async purchaseVendible(vendibleId: string)
    {
        this.isRedirecting = true;
        try
        {
            await usePaymentStore().purchaseVendible(vendibleId)
        }
        catch (error)
        {
            this.isRedirecting = false;
            useErrorStore().setError(error);
        }
    }

    cancel()
    {
        this.$router.go(-1);
    }
}
</script>
